import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { InputRadio, InputRadioProps } from '@creditornot/cb-radio';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

export type CustomRadioCardProps = {
  checked?: boolean;
  name?: string;
  value?: string | string[] | number;
  children?: React.ReactNode;
  inputProps?: Partial<InputRadioProps>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  className?: string;
  isRecommended?: boolean;
};

const StyledRoot = styled.div<{ checked?: boolean }>`
  flex: 1;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex: 1 0 100%;
  }
`;

const StyledLabel = styled.label<{ checked?: boolean; disabled?: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  align-items: center;
  z-index: 1;
  padding: 12px;
  border: 1px solid;
  border-color: ${({ checked }) => (checked ? colors.wolt100 : cssThemedValue('borderPrimary'))};
  border-radius: 8px;
  color: ${cssThemedValue('textPrimary')};
  background-color: ${cssThemedValue('surfaceMain')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &::before {
    position: absolute;
    content: '';
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background-color: ${({ checked }) => (checked ? colors.wolt8 : cssThemedValue('surfaceMain'))};
    border-radius: inherit;
    z-index: -1;
  }
`;

const StyledContent = styled.span<{ checked?: boolean }>`
  margin-inline-start: 8px;
`;

const StyledRecommendedLabel = styled.div`
  position: relative;
  background: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt8 })};
  padding: 4px;
  color: ${cssThemedValue({ light: colors.pepper100, dark: colors.salt100 })};
  border-radius: 0 0 8px 8px;
  width: 100%;
  text-align: center;
  letter-spacing: 0.5px;
  display: flex;
  align-self: center;
  justify-content: center;

  &::before {
    position: absolute;
    background: inherit;
    width: 100%;
    height: 10px;
    content: '';
    transform: translateY(-100%);
    left: 0;
    top: 0;
    z-index: 0;
  }
`;

const CustomRadioCard: React.FC<CustomRadioCardProps> = ({
  checked,
  name,
  value,
  inputProps,
  children,
  onChange,
  className,
  isRecommended = false,
  ...rest
}) => (
  <StyledRoot className={className}>
    <StyledLabel checked={checked} disabled={inputProps?.disabled}>
      <InputRadio {...inputProps} onChange={onChange} checked={checked} name={name} value={value} {...rest} />

      {children && <StyledContent checked={checked}>{children}</StyledContent>}
    </StyledLabel>
    {isRecommended && (
      <StyledRecommendedLabel>
        <Typography.Overline4>
          <FormattedMessage id="campaigns.recommended" />
        </Typography.Overline4>
      </StyledRecommendedLabel>
    )}
  </StyledRoot>
);

export default CustomRadioCard;
